/**
 * CareStart TTS speak facade — OpenAI (chartmindTTS), Gemini Flash, or Deepgram Aura,
 * always played through the iOS-unlocked Audio path in ttsService.
 *
 * WHY: CareStart had four speak switches; Deepgram/Flash used `new Audio()`
 * which breaks after Start on iOS Safari. This module is the single entry for
 * classic CareStart, CareStart3 turns, and Plus HPI.
 */

import { stripUrlsForTtsInput } from 'shared/utils/utils/stripUrlsForTtsInput';
import {
  prefetchSpeech,
  playPrefetchedAudio,
  stopSpeaking as stopOpenAiSpeaking,
  unlockAudioContext,
  TTS_PLAYBACK_STOPPED_ERROR,
} from 'services/ttsService';
import { geminiFlashTTSService } from './geminiFlashTTSService';
import { deepgramTTSService } from './deepgramTTSService';
import {
  CARESTART_DEFAULT_DEEPGRAM_VOICE,
  CARESTART_DEFAULT_OPENAI_VOICE,
  CARESTART_DEFAULT_TTS_MODEL,
  CARESTART_DEFAULT_TTS_SPEED,
  CARESTART_TTS_GENERATION_MAX_ATTEMPTS,
  CARESTART_TTS_GENERATION_TIMEOUT_MS,
  CARESTART_TTS_PROVIDER,
} from 'features/carestart/careStartTtsConstants';

/**
 * Map BCP-47 to gpt-4o-mini-tts instructions (locale + clinical pacing).
 * Translation still drives language of the text; this only nudges pronunciation.
 * @param {string} [language]
 * @returns {string|undefined}
 */
export function buildCareStartTtsInstructions(language) {
  const tag = String(language || '').trim();
  if (!tag) {
    return 'Speak clearly at a calm clinical pace suitable for a patient interview.';
  }
  const lower = tag.toLowerCase();
  if (lower.startsWith('en')) {
    return 'Speak clearly in English at a calm clinical pace suitable for a patient interview.';
  }
  const primary = tag.split(/[-_]/)[0] || tag;
  return (
    `Speak clearly in the language of locale "${tag}" (primary language code ${primary}). ` +
    'Use a calm clinical pace suitable for a patient interview. ' +
    'Follow the language of the input text.'
  );
}

/**
 * Normalize CareStart speak-provider id (default openai).
 * @param {string|undefined} raw
 * @returns {'openai'|'gemini_flash_tts'|'deepgram'}
 */
function normalizeSpeakProvider(raw) {
  if (raw === CARESTART_TTS_PROVIDER.GEMINI_FLASH_TTS) {
    return CARESTART_TTS_PROVIDER.GEMINI_FLASH_TTS;
  }
  if (raw === CARESTART_TTS_PROVIDER.DEEPGRAM) {
    return CARESTART_TTS_PROVIDER.DEEPGRAM;
  }
  return CARESTART_TTS_PROVIDER.OPENAI;
}

/**
 * @param {Promise<T>} promise
 * @param {number} ms
 * @returns {Promise<T>}
 * @template T
 */
function withTimeout(promise, ms) {
  let timeoutId = null;
  return Promise.race([
    promise.finally(() => {
      if (timeoutId) clearTimeout(timeoutId);
    }),
    new Promise((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('tts_generation_timeout')),
        ms,
      );
    }),
  ]);
}

/**
 * Prefetch audio blob for later playback (does not play).
 *
 * @param {string} text
 * @param {Object} [options]
 * @param {string} [options.language='en-US']
 * @param {'openai'|'gemini_flash_tts'|'deepgram'} [options.provider='openai']
 * @param {string} [options.voice]
 * @param {string} [options.model]
 * @param {number} [options.speed]
 * @param {string} [options.instructions]
 * @returns {Promise<Blob|null>}
 */
export async function prefetchUtterance(text, options = {}) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const utterance = stripUrlsForTtsInput(raw);
  if (!utterance) return null;

  const provider = normalizeSpeakProvider(options.provider);
  const language = String(options.language || 'en-US').trim() || 'en-US';
  const voice =
    (typeof options.voice === 'string' && options.voice.trim()) ||
    (provider === CARESTART_TTS_PROVIDER.GEMINI_FLASH_TTS
      ? 'Aoede'
      : provider === CARESTART_TTS_PROVIDER.DEEPGRAM
        ? CARESTART_DEFAULT_DEEPGRAM_VOICE
        : CARESTART_DEFAULT_OPENAI_VOICE);
  const model = options.model || CARESTART_DEFAULT_TTS_MODEL;
  const speed =
    typeof options.speed === 'number'
      ? options.speed
      : CARESTART_DEFAULT_TTS_SPEED;
  const instructions =
    options.instructions ??
    (provider === CARESTART_TTS_PROVIDER.OPENAI && model === 'gpt-4o-mini-tts'
      ? buildCareStartTtsInstructions(language)
      : undefined);

  for (let attempt = 1; attempt <= CARESTART_TTS_GENERATION_MAX_ATTEMPTS; attempt += 1) {
    try {
      if (provider === CARESTART_TTS_PROVIDER.GEMINI_FLASH_TTS) {
        geminiFlashTTSService.configure({ voiceName: voice });
        const blobUrl = await withTimeout(
          geminiFlashTTSService.generateSpeech(utterance, { voiceName: voice }),
          CARESTART_TTS_GENERATION_TIMEOUT_MS,
        );
        if (!blobUrl) continue;
        const res = await fetch(blobUrl);
        const blob = await res.blob();
        return blob;
      }

      if (provider === CARESTART_TTS_PROVIDER.DEEPGRAM) {
        deepgramTTSService.configure({ voice });
        const blobUrl = await withTimeout(
          deepgramTTSService.generateSpeech(utterance, {
            voice,
            language,
            useCache: true,
          }),
          CARESTART_TTS_GENERATION_TIMEOUT_MS,
        );
        if (!blobUrl) continue;
        const res = await fetch(blobUrl);
        const blob = await res.blob();
        return blob;
      }

      const blob = await withTimeout(
        prefetchSpeech(utterance, language, {
          model,
          voice,
          speed,
          ...(instructions ? { instructions } : {}),
        }),
        CARESTART_TTS_GENERATION_TIMEOUT_MS,
      );
      if (blob) return blob;
    } catch (err) {
      console.warn(
        `[careStartSpeakUtterance] prefetch attempt ${attempt} failed`,
        err?.message || err,
      );
    }
  }
  return null;
}

/**
 * Generate + play an utterance.
 *
 * @param {Object} args
 * @param {string} args.text
 * @param {string} [args.language='en-US']
 * @param {'openai'|'gemini_flash_tts'|'deepgram'} [args.provider='openai']
 * @param {string} [args.voice]
 * @param {string} [args.model]
 * @param {number} [args.speed]
 * @param {string} [args.instructions]
 * @param {Blob} [args.prefetchedBlob] - Skip generation when set
 * @param {function} [args.onStart]
 * @param {function} [args.onEnd]
 * @param {function} [args.onError]
 * @returns {Promise<{ success: boolean, provider?: string, reason?: string }>}
 */
export async function speakUtterance({
  text,
  language = 'en-US',
  provider = CARESTART_TTS_PROVIDER.OPENAI,
  voice,
  model = CARESTART_DEFAULT_TTS_MODEL,
  speed = CARESTART_DEFAULT_TTS_SPEED,
  instructions,
  prefetchedBlob = null,
  onStart,
  onEnd,
  onError,
} = {}) {
  try {
    unlockAudioContext();
  } catch {
    // best-effort
  }

  const activeProvider = normalizeSpeakProvider(provider);

  let audioBlob = prefetchedBlob;
  if (!audioBlob) {
    audioBlob = await prefetchUtterance(text, {
      language,
      provider: activeProvider,
      voice,
      model,
      speed,
      instructions,
    });
  }

  if (!audioBlob) {
    const err = new Error('TTS generation returned no audio');
    onError?.(err);
    return { success: false, reason: err.message, provider: activeProvider };
  }

  try {
    await playPrefetchedAudio(audioBlob, { onStart, onEnd, onError });
    return { success: true, provider: activeProvider };
  } catch (err) {
    if (err?.message === TTS_PLAYBACK_STOPPED_ERROR) {
      return { success: false, reason: TTS_PLAYBACK_STOPPED_ERROR, provider: activeProvider };
    }
    console.warn('[careStartSpeakUtterance] playback failed', err?.message || err);
    return {
      success: false,
      reason: err?.message || 'playback-failed',
      provider: activeProvider,
    };
  }
}

/**
 * Stop CareStart TTS (shared playback + Flash/Deepgram generate stop).
 */
export function stopSpeaking() {
  try {
    stopOpenAiSpeaking();
  } catch {
    // ignore
  }
  try {
    geminiFlashTTSService.stop();
  } catch {
    // ignore
  }
  try {
    deepgramTTSService.stop();
  } catch {
    // ignore
  }
}

const careStartSpeakUtterance = {
  speakUtterance,
  prefetchUtterance,
  stopSpeaking,
  buildCareStartTtsInstructions,
};

export default careStartSpeakUtterance;
